import type { ReactNode, JSX } from "react";

import { ChangeValue } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberSupportNegativeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { CenteredFlexRow, CenteredFlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import ChannelsOverviewBarChartLegend from "../../../ChannelsOverviewBarChart/components/channels-overview-bar-chart-legend/ChannelsOverviewBarChartLegend";
import { Bullet, Label } from "../../../ChannelsOverviewBarChart/components/view-by-domain-chart/StyledComponents";
import type { IDomainsDataSources } from "../../../ChannelsOverviewBarChart/types";
import { TrafficSource, TrafficSourceDisplayMap } from "../../../ChannelsOverviewBarChart/types";
import type { IChartRawData } from "../../types";

import type { IDomainPopData, ISeries, IData, IDomainsChangeValues } from "./types";

export const convertChartRawDataToDomainPopData = (
    chartRawData: IChartRawData,
    trafficSources: string[] = Object.values(TrafficSource),
): IDomainPopData => {
    const domainPopData: IDomainPopData = {};

    trafficSources.forEach((source) => {
        const data: IDomainsDataSources = {};
        const comparedData: IDomainsDataSources = {};

        Object.keys(chartRawData.Data).forEach((key) => {
            data[key] = chartRawData.Data[key][source];
        });

        Object.keys(chartRawData.ComparedData).forEach((key) => {
            comparedData[key] = chartRawData.ComparedData[key][source];
        });

        domainPopData[source] = {
            data: data,
            comparedData: comparedData,
        };
    });

    return domainPopData;
};

const getYDomainChangepoint = (dataValue: number, comparedDataValue: number, isPercentage: boolean) => {
    if (isPercentage) {
        const percentageChange = comparedDataValue !== 0 ? (dataValue - comparedDataValue) / comparedDataValue : 0;
        return percentageChange;
    } else {
        const absoluteChange = dataValue - comparedDataValue;
        return absoluteChange;
    }
};

export const processDataDomainToShow = (
    data: IDomainPopData,
    isPercentage: boolean,
    trafficSourcesMap: Record<string, { text: string; color: string }> = TrafficSourceDisplayMap,
): ISeries[] => {
    return Object.entries(trafficSourcesMap).reduce((acc: ISeries[], [key, source]) => {
        const { text, color } = source;
        const sourceData = data[key as keyof IDomainPopData];
        if (!sourceData) {
            return acc; // Skip this source if it doesn't exist in the data
        }

        const seriesData: IData[] = Object.keys(sourceData.data).map((domain) => ({
            key: domain,
            y: getYDomainChangepoint(sourceData.data[domain], sourceData.comparedData[domain], isPercentage),
            values: [
                {
                    key: domain,
                    value: sourceData.comparedData[domain],
                },
                {
                    key: domain,
                    value: sourceData.data[domain],
                },
            ],
        }));

        acc.push({
            data: seriesData,
            id: text,
            name: text,
            color: color,
        });

        return acc;
    }, [] as ISeries[]);
};

export const getLegends = (legends: ILegendItems, onLegendClick: (filter) => void): JSX.Element | null => {
    return (
        legends &&
        legends.length > 0 && (
            <ChannelsOverviewBarChartLegend
                legendItems={legends}
                onLegendsChange={onLegendClick}
                titles={[translate("mmx.channelsoverview.domain.view.legend.title"), ""]}
            />
        )
    );
};

export const getCategoriesItems = (
    series: IData[],
    domainsChangeValues: IDomainsChangeValues,
    isPercentageMode: boolean,
): ReactNode[] => {
    const numberFilter = isPercentageMode ? percentageSignFilter : abbrNumberSupportNegativeFilter;

    const sitesForLegend = chosenSites.sitelistForLegend();
    return series.map((item, index) => (
        <CenteredFlexColumn key={index}>
            <CenteredFlexRow gap={"8px"}>
                <Bullet color={sitesForLegend[index]?.color} />
                <Label>{item.key}</Label>
            </CenteredFlexRow>
            <ChangeValue
                // @ts-expect-error FIXME: functions "percentageSignFilter" and "abbrNumberSupportNegativeFilter" have different type of arguments
                value={numberFilter()(domainsChangeValues[item.key], 2)}
                isDecrease={domainsChangeValues[item.key] < 0}
            />
        </CenteredFlexColumn>
    ));
};

const calculateChangeValue = (currentSum: number, compareSum: number, isPercentageMode: boolean): number => {
    if (isPercentageMode) {
        if (compareSum === 0) {
            return currentSum === 0 ? 0 : Infinity;
        }
        return (currentSum - compareSum) / compareSum;
    } else {
        return currentSum - compareSum;
    }
};

/**
 * @returns A mapping of domain names to their calculated change values.
 */
export const calculateChangeValuesPerDomain = (
    chartData: ISeries[],
    isPercentageMode: boolean,
): IDomainsChangeValues => {
    const domainSums = new Map<string, { currentPeriodSum: number; comparePeriodSum: number }>();

    chartData.forEach((series) => {
        series.data.forEach((dataPoint) => {
            const domain = dataPoint.key;
            const comparePeriodValue = dataPoint.values[0]?.value ?? 0;
            const currentPeriodValue = dataPoint.values[1]?.value ?? 0;

            let sums = domainSums.get(domain);
            if (!sums) {
                sums = { currentPeriodSum: 0, comparePeriodSum: 0 };
                domainSums.set(domain, sums);
            }

            sums.currentPeriodSum += currentPeriodValue;
            sums.comparePeriodSum += comparePeriodValue;
        });
    });

    const changeValues: IDomainsChangeValues = {};

    domainSums.forEach((sums, domain) => {
        const { currentPeriodSum, comparePeriodSum } = sums;
        const changeValue = calculateChangeValue(currentPeriodSum, comparePeriodSum, isPercentageMode);
        changeValues[domain] = changeValue;
    });

    return changeValues;
};
